import React from 'react'
import { AlertCircle, Info } from 'lucide-react'
import { cn } from './cn'

/**
 * 語意色（design-guideline §2.5）。`danger` = 破壞與錯誤（深紅）；`warn` = 需要
 * 確認但系統沒有壞掉（赭石框線與 icon、極淡赭石底、墨色文字）。
 */
export type AlertTone = 'danger' | 'warn'

export interface AlertProps extends React.HTMLAttributes<HTMLParagraphElement> {
  /** .show → display:block。預設 true（多數用法為 "alert show"；條件渲染時可省略此 prop） */
  show?: boolean
  tone?: AlertTone
}

const TONE_CLASS = {
  danger: 'border-error/30 bg-error-soft text-error',
  warn: 'border-warn/30 bg-warn-soft text-foreground',
} as const

/**
 * 訊息列：左側 icon ＋ 文字，兩種語意色（tone）。
 * `danger`（預設）是唯一語意紅的錯誤訊息，預設 role="alert"。
 * `warn` 是非阻斷的提醒，預設 role="status"（role="alert" 會被讀屏搶讀，提醒不該搶）。
 * 兩種 tone 都必帶 icon（§2.5：不能只靠顏色傳達）。role 可由呼叫端覆寫。
 */
export function Alert({ show = true, tone = 'danger', className, ...rest }: AlertProps) {
  const Icon = tone === 'warn' ? Info : AlertCircle
  return (
    <p
      role={tone === 'warn' ? 'status' : 'alert'}
      {...rest}
      className={cn(
        'flex items-start gap-2 rounded-[var(--radius)] border px-3 py-2 text-sm',
        TONE_CLASS[tone],
        !show && 'hidden',
        className
      )}
    >
      <Icon
        aria-hidden="true"
        className={cn('mt-px size-4 shrink-0', tone === 'warn' && 'text-warn')}
      />
      <span>{rest.children}</span>
    </p>
  )
}
